import type { RecaptchaEndpointConfig, RecaptchaAction, HttpMethod } from './utils/types';
import { createAction } from './utils/types';

/**
 * RecaptchaRegistry - Registry for dynamic endpoint registration/unregistration
 *
 * @example
 * // Register with Builder pattern
 * recaptchaRegistry
 *   .endpoint("/api/checkout")
 *   .action("checkout")
 *   .methods("POST")
 *   .domain("checkout")
 *   .register();
 *
 * // Unregister at runtime
 * recaptchaRegistry.unregister("/api/checkout");
 */
class RecaptchaRegistry {
  private configs: Map<string, RecaptchaEndpointConfig> = new Map();

  /**
   * Register endpoint configuration
   * @param config - Endpoint configuration to register
   * @returns this (supports method chaining)
   */
  public register(config: RecaptchaEndpointConfig): this {
    const key = this.getConfigKey(config.pattern);

    const normalizedConfig: RecaptchaEndpointConfig = {
      ...config,
      priority: config.priority ?? 0,
      enabled: config.enabled ?? true,
    };

    this.configs.set(key, normalizedConfig);
    return this;
  }

  /**
   * Register multiple endpoints at once
   * @param configs - Array of endpoint configurations to register
   * @returns this (supports method chaining)
   */
  public registerAll(configs: RecaptchaEndpointConfig[]): this {
    configs.forEach((config) => this.register(config));
    return this;
  }

  /**
   * Unregister endpoint configuration
   * @param pattern - URL pattern of the endpoint to unregister (string or RegExp)
   * @returns Whether unregistration was successful
   */
  public unregister(pattern: string | RegExp): boolean {
    const key = this.getConfigKey(pattern);
    return this.configs.delete(key);
  }

  /**
   * Unregister all endpoints belonging to a specific domain
   * @param domain - Domain name to unregister (e.g., 'cart', 'auth', 'product')
   * @returns Number of unregistered endpoints
   */
  public unregisterByDomain(domain: string): number {
    let count = 0;

    for (const [key, config] of this.configs) {
      if (config.metadata?.domain === domain) {
        this.configs.delete(key);
        count++;
      }
    }

    return count;
  }

  /**
   * Enable or disable an endpoint
   * @param pattern - URL pattern of the target endpoint
   * @param enabled - Whether to enable (true) or disable (false)
   * @returns Whether the configuration change was successful (true if pattern exists)
   */
  public setEnabled(pattern: string | RegExp, enabled: boolean): boolean {
    const key = this.getConfigKey(pattern);
    const config = this.configs.get(key);

    if (!config) return false;

    config.enabled = enabled;
    return true;
  }

  /**
   * Get reCAPTCHA action for a request path and HTTP method
   * @param path - API request path (e.g., '/cart', '/auth/login')
   * @param method - HTTP method (e.g., 'GET', 'POST')
   * @returns Matched RecaptchaAction or null if no match
   */
  public getActionForPath(path: string, method?: string): RecaptchaAction | null {
    for (const config of this.getSortedConfigs()) {
      if (!config.enabled) continue;

      const pathMatches =
        typeof config.pattern === 'string'
          ? path.startsWith(config.pattern)
          : config.pattern.test(path);

      if (!pathMatches) continue;

      if (config.methods && method) {
        const upperMethod = method.toUpperCase() as HttpMethod;
        if (!config.methods.includes(upperMethod)) continue;
      }

      return config.action;
    }

    return null;
  }

  /**
   * Start endpoint configuration with Builder pattern
   * @param pattern - URL pattern (string: prefix match, RegExp: regex match)
   * @returns EndpointBuilder instance
   */
  public endpoint(pattern: string | RegExp): EndpointBuilder {
    return new EndpointBuilder(this, pattern);
  }

  /**
   * Get all registered endpoint configurations
   * @returns Array of all RecaptchaEndpointConfig
   */
  public getAll(): RecaptchaEndpointConfig[] {
    return Array.from(this.configs.values());
  }

  /**
   * Clear all configurations
   */
  public clear(): void {
    this.configs.clear();
  }

  /**
   * Number of registered endpoints
   */
  public get size(): number {
    return this.configs.size;
  }

  private getConfigKey(pattern: string | RegExp): string {
    return typeof pattern === 'string' ? pattern : pattern.source;
  }

  private getSortedConfigs(): RecaptchaEndpointConfig[] {
    return Array.from(this.configs.values())
      .filter((c) => c.enabled)
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }
}

/**
 * EndpointBuilder - Builder class for declarative endpoint configuration
 * Create instance via recaptchaRegistry.endpoint()
 */
class EndpointBuilder {
  private config: Partial<RecaptchaEndpointConfig>;

  constructor(
    private registry: RecaptchaRegistry,
    pattern: string | RegExp,
  ) {
    this.config = { pattern };
  }

  /** Set reCAPTCHA action name */
  public action(action: string | RecaptchaAction): this {
    this.config.action = typeof action === 'string' ? createAction(action) : action;
    return this;
  }

  /** Set allowed HTTP methods (allows all methods if not set) */
  public methods(...methods: HttpMethod[]): this {
    this.config.methods = methods;
    return this;
  }

  /** Set matching priority (higher = matched first, default: 0) */
  public priority(priority: number): this {
    this.config.priority = priority;
    return this;
  }

  /** Set domain identifier (used for group unregistration) */
  public domain(domain: string): this {
    this.config.metadata = { ...this.config.metadata, domain };
    return this;
  }

  /** Set description metadata */
  public description(description: string): this {
    this.config.metadata = { ...this.config.metadata, description };
    return this;
  }

  /** Set initial enabled state (default: true) */
  public enabled(enabled: boolean): this {
    this.config.enabled = enabled;
    return this;
  }

  /** Register configuration to registry and return registry (supports chaining) */
  public register(): RecaptchaRegistry {
    if (!this.config.action) {
      throw new Error('[RecaptchaRegistry] Action is required for endpoint registration');
    }

    this.registry.register(this.config as RecaptchaEndpointConfig);
    return this.registry;
  }
}

export const recaptchaRegistry = new RecaptchaRegistry();
